import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck } = FiIcons;

const industries = ["Banking", "Insurance", "Manufacturing", "Healthcare", "Oil & Gas"];
const benefits = [
  "Customised Compliance Made Easy: Our vCISO tooling allows anyone to align with every security and governance framework effortlessly.",
  "Privacy Experts: Our data lifecycle management solutions are built to keep your business and customer data secure.",
  "Decades of Expertise: Our seasoned professionals simplify even the most complex challenges.",
  "Proactive & Responsive: Whether planning for the future or tackling emergencies, we've got your back.",
  "Client-Focused: Your success is our mission. Every solution is tailored to your specific needs.",
];

export default function WhyChooseUs() {
  return (
    <section id="why-us" className="pt-24 pb-24 md:pt-32 md:pb-32 bg-[#0d0d0d] border-t border-[#222222]">
      <div className="container mx-auto px-8">
        <div className="flex flex-col lg:flex-row gap-20 items-center">
          <motion.div
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h5 className="text-brand-orange font-bold tracking-widest uppercase text-sm mb-4">Why Choose Us</h5>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">Trusted Across Industries</h2>
            <div className="flex flex-wrap gap-3 mb-10">
              {industries.map(industry => (
                <span key={industry} className="px-5 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full text-sm font-medium text-gray-300">
                  {industry}
                </span>
              ))}
            </div>
            <ul className="space-y-6 mb-10">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-4">
                  <div className="mt-1 w-6 h-6 rounded-full bg-brand-orange/20 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="text-brand-orange text-sm" />
                  </div>
                  <p className="text-gray-300 leading-relaxed text-sm md:text-base">{benefit}</p>
                </li>
              ))}
            </ul>
            <a href="#contact" className="btn-primary">Get Started</a>
          </motion.div>

          <motion.div
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <DashboardGraphic />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function DashboardGraphic() {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-[#2a2a2a] shadow-2xl bg-[#111111]" style={{ animation: 'float 6s ease-in-out infinite' }}>
      <style>{`@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-16px)}}`}</style>
      <div className="bg-[#1a1a1a] px-4 py-3 flex items-center gap-2 border-b border-[#2a2a2a]">
        <div className="w-3 h-3 rounded-full bg-red-500/70" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
        <div className="w-3 h-3 rounded-full bg-green-500/70" />
        <span className="ml-3 text-xs text-gray-500 font-mono">security-dashboard.minotaur</span>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Threats Blocked', value: '2,847', color: 'text-green-400' },
            { label: 'Compliance Score', value: '98%', color: 'text-brand-orange' },
            { label: 'Alerts Today', value: '3', color: 'text-yellow-400' },
          ].map((stat, i) => (
            <div key={i} className="bg-[#1a1a1a] rounded-xl p-3 text-center border border-[#2a2a2a]">
              <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-gray-500 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-gray-400 text-xs font-semibold mb-3 uppercase tracking-wider">Framework Compliance</p>
          {[{ name: 'ISO 27001', pct: 96 }, { name: 'GDPR', pct: 100 }, { name: 'NIS2', pct: 89 }, { name: 'DORA', pct: 82 }].map((item, i) => (
            <div key={i} className="mb-3 last:mb-0">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-300">{item.name}</span>
                <span className="text-brand-orange font-semibold">{item.pct}%</span>
              </div>
              <div className="h-1.5 bg-[#2a2a2a] rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-brand-orange" style={{ width: `${item.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-gray-400 text-xs font-semibold mb-3 uppercase tracking-wider">Recent Activity</p>
          {[
            { msg: 'Vulnerability scan completed', time: '2m ago', dot: 'bg-green-400' },
            { msg: 'GDPR audit report generated', time: '1h ago', dot: 'bg-brand-orange' },
            { msg: 'Threat detected & blocked', time: '3h ago', dot: 'bg-yellow-400' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-[#2a2a2a] last:border-0">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.dot}`} />
              <p className="text-gray-300 text-xs flex-1">{item.msg}</p>
              <span className="text-gray-600 text-xs">{item.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}